import { useState } from "react";
import { Col, Typography, Space, Flex, Upload } from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Table } from "@/components/v2";
import { Button, Card, Input, InputNumber } from "@rumbledirect/ui";

const { Title, Text } = Typography;

interface LineItem {
  id: number;
  description: string;
  amount: number | null;
  document?: { name: string; url: string };
}

export const ClaimDossierFinancialTab = () => {
  const [insuranceType, setInsuranceType] = useState<string>();
  const [coverageStatus, setCoverageStatus] = useState<string>();
  const [liability, setLiability] = useState<string>();
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: 1, description: "Vervangend vervoer", amount: null },
    { id: 2, description: "Berging, transport & stalling", amount: null },
    { id: 3, description: "Administratie", amount: null },
    { id: 4, description: "Expertise kosten", amount: null },
  ]);

  const RadioOption = ({
    label,
    subLabel,
    selected,
    onClick,
  }: {
    value: string;
    label: string;
    subLabel: string;
    selected: boolean;
    onClick: () => void;
  }) => (
    <div
      onClick={onClick}
      className={`cursor-pointer border rounded-lg p-4 text-lg transition-all flex items-start gap-4 ${
        selected ? "border-primary-600 bg-primary-900" : "border-gray-300"
      } hover:border-primary-400`}
    >
      {/* Radio rondje */}
      <div className="mt-1">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            selected ? "border-primary-600" : "border-gray-400"
          }`}
        >
          {selected && (
            <div className="w-2.5 h-2.5 bg-primary-600 rounded-full" />
          )}
        </div>
      </div>

      {/* Label en sublabel */}
      <div>
        <div className="font-medium">{label}</div>
        {subLabel && (
          <div
            className={`text-sm mt-1 ${selected ? "text-black" : "text-gray-400"}`}
          >
            {subLabel}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Flex gap={20} className="w-full !px-0">
      <Col flex="auto" className="!px-0">
        <Card title="Contractgegevens" className="w-full mb-6">
          <div className="mb-8">
            <Title level={4}>Eigen risico</Title>

            <Text className="text-gray-600 block mb-4">
              Selecteer het juiste eigen risico voor deze claim
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RadioOption
                value="deductible_ja"
                label="Ja"
                subLabel=""
                selected={insuranceType === "deductible_ja"}
                onClick={() => setInsuranceType("deductible_ja")}
              />
              <RadioOption
                value="deductible_no"
                label="Nee"
                subLabel=""
                selected={insuranceType === "deductible_no"}
                onClick={() => setInsuranceType("deductible_no")}
              />
            </div>
          </div>

          {/* Dekking */}
          {insuranceType === "deductible_ja" && (
            <div className="mb-8">
              <Title level={4}>Eigen risico bedrag</Title>
              <Text className="text-gray-600 block mb-4">
                Selecteer het juiste eigen risico bedrag voor deze claim
              </Text>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RadioOption
                  value="in_beoordeling"
                  subLabel=""
                  label="€350"
                  selected={coverageStatus === "in_beoordeling"}
                  onClick={() => setCoverageStatus("in_beoordeling")}
                />
                <RadioOption
                  value="gedekt"
                  subLabel=""
                  label="€500"
                  selected={coverageStatus === "gedekt"}
                  onClick={() => setCoverageStatus("gedekt")}
                />
                <RadioOption
                  value="voorbehoud"
                  subLabel=""
                  label="€750"
                  selected={coverageStatus === "voorbehoud"}
                  onClick={() => setCoverageStatus("voorbehoud")}
                />
                <RadioOption
                  value="afgewezen"
                  subLabel=""
                  label="€1500"
                  selected={coverageStatus === "afgewezen"}
                  onClick={() => setCoverageStatus("afgewezen")}
                />
              </div>
            </div>
          )}

          {/* Aansprakelijkheid */}
          <div className="mb-8">
            <Title level={4}>Vervangend vervoer</Title>
            <Text className="text-gray-600 block mb-4">
              Selecteer hoe de kosten voor vervangend vervoer worden doorbelast
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RadioOption
                value="eigen_schuld"
                label="Doorbelasten"
                subLabel="Kosten worden doorbelast aan de verzekerde"
                selected={liability === "eigen_schuld"}
                onClick={() => setLiability("eigen_schuld")}
              />
              <RadioOption
                value="tegenpartij"
                label="In contract"
                subLabel="In het leasecontract is vervangend vervoer inbegrepen"
                selected={liability === "tegenpartij"}
                onClick={() => setLiability("tegenpartij")}
              />
            </div>
          </div>
        </Card>

        {/* Line Items Card */}
        <Card className="w-full mt-4" title="Kostenoverzicht">
          <Table
            dataSource={lineItems}
            pagination={false}
            rowKey="id"
            columns={[
              {
                title: "Omschrijving",
                dataIndex: "description",
                key: "description",
                render: (text: string, record: LineItem) => (
                  <Input
                    value={text}
                    onChange={(e) => {
                      const newItems = lineItems.map((item) =>
                        item.id === record.id
                          ? { ...item, description: e.target.value }
                          : item,
                      );
                      setLineItems(newItems);
                    }}
                    placeholder="Omschrijving"
                  />
                ),
              },
              {
                title: "Bedrag",
                dataIndex: "amount",
                key: "amount",
                width: 150,
                render: (amount: number | null, record: LineItem) => (
                  <InputNumber
                    value={amount}
                    onChange={(value) => {
                      const newItems = lineItems.map((item) =>
                        item.id === record.id
                          ? { ...item, amount: value }
                          : item,
                      );
                      setLineItems(newItems);
                    }}
                    prefix="€"
                    placeholder="0,00"
                    className="w-full"
                    min={0}
                    precision={2}
                    decimalSeparator=","
                  />
                ),
              },
              {
                title: "Document",
                key: "document",
                width: 150,
                render: (_, record: LineItem) => (
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const newItems = lineItems.map((item) =>
                        item.id === record.id
                          ? {
                              ...item,
                              document: {
                                name: file.name,
                                url: URL.createObjectURL(file),
                              },
                            }
                          : item,
                      );
                      setLineItems(newItems);
                      return false;
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      size="small"
                      type={record.document ? "default" : "dashed"}
                    >
                      {record.document
                        ? record.document.name.length > 10
                          ? `${record.document.name.substring(0, 10)}...`
                          : record.document.name
                        : "Upload"}
                    </Button>
                  </Upload>
                ),
              },
              {
                title: "",
                key: "actions",
                width: 50,
                render: (_, record: LineItem) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setLineItems(
                        lineItems.filter((item) => item.id !== record.id),
                      );
                    }}
                  />
                ),
              },
            ]}
          />
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              const newId =
                lineItems.length > 0
                  ? Math.max(...lineItems.map((item) => item.id)) + 1
                  : 1;
              setLineItems([
                ...lineItems,
                { id: newId, description: "", amount: null },
              ]);
            }}
            className="w-full mt-4"
          >
            Regel toevoegen
          </Button>
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <Text strong className="text-lg">
              Totaal
            </Text>
            <Text strong className="text-lg">
              €
              {lineItems
                .reduce((sum, item) => sum + (item.amount || 0), 0)
                .toFixed(2)
                .replace(".", ",")}
            </Text>
          </div>
        </Card>
      </Col>

      <Col className="!px-0 2xl:min-w-[400px] min-w-[300px]">
        <Card className="w-full" title={"Acties"}>
          <Space direction="vertical" className="w-full">
            <Button
              type="primary"
              size="large"
              className="w-full"
              icon={<CheckOutlined className="!text-[17px]" />}
            >
              Claimverwerking voltooien
            </Button>
          </Space>
        </Card>
      </Col>
    </Flex>
  );
};
